import React, { memo, useEffect, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import { TabsSkeleton } from '@atlassian/jira-horizontal-nav-common/src/ui/horizontal-nav-skeleton/index.tsx';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import type { JiraHorizontalNavigationContent$key } from '@atlassian/jira-relay/src/__generated__/JiraHorizontalNavigationContent.graphql';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { getFlagDetails } from '@atlassian/jira-horizontal-nav-common/src/common/get-flag-details/getFlagDetails.tsx';
import { showFlag } from '@atlassian/jira-flags/src/services/index.tsx';
import { HANDLED_STATUS_CODES } from '@atlassian/jira-horizontal-nav-common/src/constants.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import type { JiraHorizontalNavigationContentOld$key } from '@atlassian/jira-relay/src/__generated__/JiraHorizontalNavigationContentOld.graphql.ts';
import TabsContent from './TabsContent.tsx';
import NavWrapper from './NavWrapper.tsx';
import NavHeader from './header/NavHeader.tsx';
import { CustomTemplateProjectBanner } from './CustomTemplateProjectBanner.tsx';
import { JiraHorizontalNavigationContentOld } from './JiraHorizontalNavigationContentOld.tsx';

const JiraHorizontalNavigationContentComponent = ({
	queryRef,
}: {
	queryRef: JiraHorizontalNavigationContent$key;
}): React.JSX.Element | null => {
	const data = useFragment<JiraHorizontalNavigationContent$key>(
		graphql`
			fragment JiraHorizontalNavigationContent on Query
			@argumentDefinitions(
				isJiraCustomTemplatesUxEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-custom-templates-ux-fg.relayprovider"
				}
			) {
				...TabsContent_query
				...NavHeader_query
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectIdOrKey)
					@include(if: $withProject) {
					...TabsContent_project
					...NavHeader_project
					...CustomTemplateProjectBanner @include(if: $isJiraCustomTemplatesUxEnabled)
					softwareBoardCount
					__id
				}
				jira {
					containerNavigation(input: $containerNavigationInput)
						@required(action: THROW)
						@optIn(to: "JiraContainerNavigation") {
						... on JiraContainerNavigation {
							scopeId
						}
						... on QueryError {
							extensions {
								statusCode
							}
						}
					}
				}
			}
		`,
		queryRef,
	);

	const project = data.jira_projectByIdOrKey;
	const [hasError, setHasError] = useState<boolean>(false);

	const [isFullscreen] = useIsFullscreen();

	const mostRecentlyViewedBoard =
		data?.jira?.containerNavigation?.scopeId &&
		project?.softwareBoardCount &&
		project.softwareBoardCount > 0
			? {
					scopeId: data.jira.containerNavigation.scopeId,
					projectId: project?.__id,
				}
			: undefined;

	useEffect(() => {
		const statusCode = data.jira?.containerNavigation?.extensions?.[0]?.statusCode;

		if (statusCode && HANDLED_STATUS_CODES.includes(statusCode)) {
			const flagDetails = getFlagDetails(statusCode);
			showFlag(flagDetails);
			setHasError(true);
		}
	}, [data]);

	// Some routes with invalid project scope or scope Id request for horizontal navigation
	// E.g. JSM projects do not support horizontal navigation, but connect app routes in JSM will request for it
	// These cases are unprocessable and return a dedicated 422 error, so we can skip rendering and return null
	// 401, 403, 404 errors are handled by throwing flag and we can return null - same as in TabsContent
	if (
		data.jira?.containerNavigation?.extensions?.[0]?.statusCode === 422 ||
		data.jira?.containerNavigation?.extensions?.[0]?.statusCode === 404 ||
		hasError
	) {
		return null;
	}

	return (
		<>
			{project && fg('jira_custom_templates_ux') && (
				<CustomTemplateProjectBanner projectFragment={project} />
			)}
			<NavWrapper mostRecentlyViewedBoard={mostRecentlyViewedBoard}>
				<NavHeader projectFragment={project} queryFragment={data} />
				<JSErrorBoundary
					id="HorizontalNavTabContentErrorBoundary"
					packageName="jiraHorizontalNav"
					fallback={TabsSkeleton}
				>
					{!isFullscreen ? <TabsContent projectFragment={project} queryFragment={data} /> : null}
				</JSErrorBoundary>
			</NavWrapper>
		</>
	);
};

const JiraHorizontalNavigationContent: React.NamedExoticComponent<
	{
		queryRef: JiraHorizontalNavigationContent$key;
	} & {
		queryRef: JiraHorizontalNavigationContentOld$key;
	}
> = memo(
	componentWithFG(
		'blu-7947_fix_board_not_found_errors_horizontal_nav',
		JiraHorizontalNavigationContentComponent,
		JiraHorizontalNavigationContentOld,
	),
);

export default JiraHorizontalNavigationContent;
